import React, { useState } from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import { AlertButton, AlertOptions } from './types';
import { useThemeApp } from 'components/Theme/theme';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Help, Info, Close, Warning } from '@mui/icons-material';
// import { ThemedInput } from '@/components/input/input/ThemedInput';
import { Box, Fade, alpha, Button, styled, Dialog, useTheme, Backdrop, DialogTitle, DialogActions, DialogContent, CircularProgress, DialogContentText } from '@mui/material';

interface ThemedAlertProps {
    open: boolean;
    onClose: () => void;
    options: AlertOptions;
}

const StyledButton = styled(Button)(({ theme }) => ({
    minHeight: 44,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: '1rem',
    textTransform: 'none',
    transition: 'all 0.2s ease',
    '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows[3],
    },
}));

export const ThemedAlert: React.FC<ThemedAlertProps> = ({ open, onClose, options }) => {
    const theme = useTheme();
    const { colors } = useThemeApp();
    const [loadingButton, setLoadingButton] = useState<number | null>(null);


    if (!options) {
        return null;
    }

    const {
        title,
        input,
        message,
        maxWidth = 'xs',
        canClose = true,
        hideIcon = false,
        type = 'default',
        disableEscapeKey = false,
        disableBackdropClick = false,
        buttons = [{ text: 'OK', onPress: () => { }, style: 'primary' }],
    } = options;

    const getTypeConfig = () => {
        switch (type) {
            case 'ERROR':
                return {
                    color: colors.danger,
                    bgColor: alpha(colors.danger, 0.1),
                    icon: <ErrorIcon sx={{ fontSize: 48 }} />,
                };
            case 'SUCCESS':
                return {
                    color: colors.success,
                    bgColor: alpha(colors.success, 0.1),
                    icon: <ThumbUpIcon sx={{ fontSize: 48 }} />,
                };
            case 'WARNING':
                return {
                    color: theme.palette.warning.main,
                    icon: <Warning sx={{ fontSize: 48 }} />,
                    bgColor: alpha(theme.palette.warning.main, 0.1),
                };
            case 'INFO':
                return {
                    color: theme.palette.info.main,
                    icon: <Info sx={{ fontSize: 48 }} />,
                    bgColor: alpha(theme.palette.info.main, 0.1),
                };
            default:
                return {
                    color: colors.primary,
                    icon: <Help sx={{ fontSize: 48 }} />,
                    bgColor: alpha(colors.primary, 0.1),
                };
        }
    };

    const typeConfig = getTypeConfig();

    const handleButtonClick = async (button: AlertButton, index: number) => {
        if (button.loading || button.disabled) return;

        try {
            setLoadingButton(index);

            if (button.onPress)
                await button.onPress();

            if (canClose) {
                onClose();
                options.onClose?.();
            }
        } catch (error) {
            console.error('Erro no botão do alerta:', error);
        } finally {
            setLoadingButton(null);
        }
    };

    const handleClose = (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (
            (reason === 'backdropClick' && disableBackdropClick) ||
            (reason === 'escapeKeyDown' && disableEscapeKey)
        ) {
            return;
        }

        if (canClose) {
            onClose();
            options.onClose?.();
        }
    };

    const getButtonColor = (style: AlertButton['style']) => {
        switch (style) {
            case 'cancel':
                return 'error';
            case 'secondary':
                return 'secondary';
            case 'text':
                return 'inherit';
            default:
                return 'primary';
        }
    };

    const getButtonVariant = (style: AlertButton['style']) => {
        switch (style) {
            case 'text':
                return 'text';
            case 'cancel':
            case 'secondary':
                return 'outlined';
            default:
                return 'contained';
        }
    };

    return (
        <Dialog
            fullWidth
            open={open}
            maxWidth={maxWidth}
            onClose={handleClose}
            TransitionComponent={Fade}
            BackdropComponent={Backdrop}
            BackdropProps={{
                sx: {
                    backgroundColor: colors.background + 50,
                    backdropFilter: 'blur(4px)',
                },
            }}
            PaperProps={{
                sx: {
                    m: 2,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: theme.shadows[24],
                    minWidth: { sm: '60dvw', md: '40dvw', xl: '20dvw' },
                },
            }}
        >
            <Box sx={{ position: 'relative' }}>
                {canClose && (
                    <Button
                        onClick={onClose}
                        sx={{
                            top: 8,
                            right: 8,
                            zIndex: 1,
                            width: 32,
                            padding: 0,
                            height: 32,
                            minWidth: 'auto',
                            borderRadius: '50%',
                            position: 'absolute',
                        }}
                    >
                        <Close />
                    </Button>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        p: { xs: 3, sm: 4 },
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    {!hideIcon && (
                        <Box
                            sx={{
                                mb: 3,
                                width: 80,
                                height: 80,
                                display: 'flex',
                                borderRadius: '50%',
                                alignItems: 'center',
                                color: typeConfig.color,
                                justifyContent: 'center',
                                bgcolor: typeConfig.bgColor,
                            }}
                        >
                            {typeConfig.icon}
                        </Box>
                    )}

                    {title && (
                        <DialogTitle
                            sx={{
                                p: 0,
                                width: '100%',
                                fontWeight: 700,
                                fontSize: '1.5rem',
                                textAlign: 'center',
                                mb: hideIcon ? 3 : 1,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {title}
                        </DialogTitle>
                    )}

                    {message && (
                        <DialogContent sx={{ p: 0, width: '100%', mb: input ? 3 : 0 }}>
                            {typeof message === 'string' ? (
                                <DialogContentText
                                    sx={{
                                        fontSize: '1rem',
                                        lineHeight: 1.6,
                                        textAlign: 'center',
                                        whiteSpace: 'pre-line',
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {message}
                                </DialogContentText>
                            ) : (
                                <Box sx={{ textAlign: 'center' }}>{message}</Box>
                            )}
                        </DialogContent>
                    )}

                    {/* {input && <ThemedInput {...input} />} */}

                    <DialogActions
                        sx={{
                            p: 0,
                            mt: 3,
                            gap: 1,
                            width: '100%',
                            flexWrap: 'wrap',
                            flexDirection: buttons.length > 2 ? 'column' : 'row',
                        }}
                    >
                        {buttons.map((button, index) => (
                            <StyledButton
                                key={index}
                                fullWidth={buttons.length > 2}
                                color={getButtonColor(button.style)}
                                variant={getButtonVariant(button.style)}
                                onClick={() => handleButtonClick(button, index)}
                                disabled={button.disabled || loadingButton !== null}
                                sx={{
                                    flex: buttons.length <= 2 ? 1 : undefined,
                                    opacity: button.disabled ? 0.6 : 1,
                                }}
                                startIcon={
                                    loadingButton === index ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : undefined
                                }
                            >
                                {loadingButton === index ? '' : button.text}
                            </StyledButton>
                        ))}
                    </DialogActions>
                </Box>
            </Box>
        </Dialog>
    );
};